import React, { useEffect, useMemo, useRef, useState } from "react";
import type { SemesterInfo, Course } from "@nelurio/contracts";
import { Badge, Button, Card, EmptyState, Field, Icon, PageHeader, SelectField } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { createPortal } from "react-dom";
import { api, isDemoMode } from "../api/client";
import { useCourses } from "../hooks/useCourses";
import { UNIVERSITIES, scaleFor, GRADING_SCALES, KNOWN_COUNTRIES, systemFor, countryOf, type GradingScale, type UniversityEntry } from "../lib/grading";
import { markTermEnd, pickTermEnd, readTermEndState, type TermEndCandidate } from "../lib/termEnd";

/** User-planned semesters, stored on the device and merged into the timeline. */
interface ModuleRow {
  name: string;
  credits: number;
}

interface PlannedSemester {
  id: string;
  name: string;
  termNumber: number;
  startsOn: string;
  endsOn?: string;
  credits: number;
  modules?: ModuleRow[];
  status: "planned" | "active" | "completed";
}

const KEY = "nelurio.semesters.v1";
const SETTINGS_KEY = "nelurio.academic.v1";
const GRADES_KEY = "nelurio.termGrades.v1";

function readPlanned(): PlannedSemester[] {
  try {
    const value = JSON.parse(window.localStorage.getItem(KEY) ?? "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function writePlanned(list: PlannedSemester[]) {
  try { window.localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

interface AcademicSettings {
  university: string;
  major: string;
  country: string;
  termEndsOn: string;
}
function readSettings(): AcademicSettings {
  try {
    return { university: "", major: "", country: "", termEndsOn: "", ...JSON.parse(window.localStorage.getItem(SETTINGS_KEY) ?? "{}") };
  } catch {
    return { university: "", major: "", country: "", termEndsOn: "" };
  }
}

function SemesterComposer({ open, editing, onClose, onSaved }: {
  open: boolean;
  editing: PlannedSemester | null;
  onClose: () => void;
  onSaved: (semester: PlannedSemester) => void;
}) {
  const { t, formatNumber } = useI18n();
  const [name, setName] = useState("");
  const [termNumber, setTermNumber] = useState("1");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [credits, setCredits] = useState("30");
  const [modules, setModules] = useState<ModuleRow[]>([]);
  const [status, setStatus] = useState<PlannedSemester["status"]>("planned");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (editing) {
      setName(editing.name);
      setTermNumber(String(editing.termNumber || 1));
      setStartsOn(editing.startsOn ?? "");
      setEndsOn(editing.endsOn ?? "");
      setCredits(String(editing.credits || ""));
      setModules(editing.modules ?? []);
      setStatus(editing.status);
    } else {
      setName("");
      setTermNumber(String(nextTermNumberHint()));
      setStartsOn("");
      setEndsOn("");
      setCredits(String(systemFor(readSettings().country || countryOf(readSettings().university))?.creditsPerTerm ?? 30));
      setModules([{ name: "", credits: 5 }]);
      setStatus("planned");
    }
  }, [open, editing]);

  // Suggest the next free term number so "which term is this" is pre-answered.
  const moduleSum = modules.reduce((sum, module) => sum + (module.credits || 0), 0);

  function nextTermNumberHint(): number {
    try {
      const existing = JSON.parse(window.localStorage.getItem("nelurio.semesters.v1") ?? "[]") as PlannedSemester[];
      return existing.reduce((max, item) => Math.max(max, item.termNumber || 0), 0) + 1;
    } catch {
      return 1;
    }
  }

  if (!open) return null;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) { setError(t("common.error")); return; }
    onSaved({
      id: editing?.id ?? crypto.randomUUID(),
      name: name.trim(),
      termNumber: Number(termNumber) || 1,
      modules: modules.filter((module) => module.name.trim() !== ""),
      startsOn,
      endsOn: endsOn || undefined,
      credits: Number(credits) || 0,
      status
    });
    onClose();
  };

  return createPortal(
    <div className="composer-backdrop" onClick={onClose} role="presentation">
      <div className="composer-modal rise" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="composer-modal__head">
          <h2>{editing ? t("sem.edit") : t("sem.add")}</h2>
          <button className="composer-modal__close" onClick={onClose} aria-label={t("common.cancel")}><Icon name="x" size={16} /></button>
        </div>
        <form onSubmit={submit}>
          <Field label={t("sem.name")} value={name} onChange={(event) => setName(event.target.value)} placeholder={t("sem.namePlaceholder")} required autoFocus />
          <Field label={t("sem.number")} type="number" value={termNumber} onChange={(event) => setTermNumber(event.target.value)} min={1} max={14} />
          <Field label={t("sem.start")} type="date" value={startsOn} onChange={(event) => setStartsOn(event.target.value)} />
          <Field label={t("sem.end")} type="date" value={endsOn} onChange={(event) => setEndsOn(event.target.value)} />
          <Field label={t("sem.credits")} type="number" value={credits} onChange={(event) => setCredits(event.target.value)} min={0} max={120} />
          <SelectField label={t("sem.status")} value={status} onChange={(event) => setStatus(event.target.value as PlannedSemester["status"])}>
            <option value="planned">{t("academic.status.planned")}</option>
            <option value="active">{t("academic.status.active")}</option>
            <option value="completed">{t("academic.status.completed")}</option>
          </SelectField>
          {error && <div className="form-error" role="alert">{error}</div>}

          <div className="module-editor">
            <div className="module-editor__head">
              <strong>{t("sem.modules")}</strong>
              <Button type="button" variant="secondary" icon="plus" onClick={() => setModules((current) => [...current, { name: "", credits: 5 }])}>{t("sem.addModule")}</Button>
            </div>
            {modules.map((module, index) => (
              <div className="module-editor__row" key={index}>
                <input
                  className="nl-input"
                  value={module.name}
                  onChange={(event) => setModules((current) => current.map((entry, i) => (i === index ? { ...entry, name: event.target.value } : entry)))}
                  placeholder={t("sem.moduleName")}
                  aria-label={t("sem.moduleName")}
                />
                <input
                  className="nl-input module-editor__credits"
                  type="number"
                  min={0}
                  max={30}
                  value={module.credits || ""}
                  onChange={(event) => setModules((current) => current.map((entry, i) => (i === index ? { ...entry, credits: Number(event.target.value) || 0 } : entry)))}
                  aria-label={t("sem.moduleCredits")}
                />
                <button type="button" className="module-editor__remove" onClick={() => setModules((current) => current.filter((_, i) => i !== index))} aria-label={t("common.delete")}><Icon name="trash" size={14} /></button>
              </div>
            ))}
            <small className={`module-editor__sum ${moduleSum !== Number(credits || 0) ? "is-off" : ""}`}>
              {t("sem.moduleSum", { sum: formatNumber(moduleSum), target: formatNumber(Number(credits) || 0) })}
            </small>
          </div>

          <div className="composer-modal__actions">
            <Button type="button" variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit">{t("common.save")}</Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

/** Game-style 3D journey map (PvZ-level vibe): floating island coins along a
 *  glowing winding trail, START and GRADUATION gates, current term pulsing.
 *  Pure CSS-3D + SVG — no dependencies. */
function TermMap({ nodes, activeId, onSelect }: {
  nodes: Array<{ id: string; label: string; sub: string; status: string }>;
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  const { t, formatNumber } = useI18n();
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [stageWidth, setStageWidth] = useState(0);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => setStageWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  if (!nodes.length) return null;

  // Zigzag positions: alternate left/right with depth jitter — like a game level path.
  // Coins are 120px wide, so x stays within 27–73% to never clip the stage;
  // vertical step 104px keeps badge+name+sub stacks clear of each other.
  const STEP = 104;
  const height = 84 + nodes.length * STEP + 120;
  const points = nodes.map((_, index) => ({
    x: 50 + (index % 2 === 0 ? -21 : 21) + ((index % 3) - 1) * 6,
    y: 84 + index * STEP
  }));
  const clampX = (x: number) => Math.min(Math.max(x, 20), 80);

  // SVG path data cannot contain percentages — x must be resolved to real pixels
  // from the measured stage width (percentages silently invalidate the path).
  const xPx = (xPercent: number) => Math.round((clampX(xPercent) / 100) * stageWidth);
  const trailPath = stageWidth
    ? points
        .map((point, index) => {
          const cx = xPx(point.x);
          if (index === 0) return `M ${cx} ${point.y}`;
          const px = xPx(points[index - 1].x);
          const midY = (points[index - 1].y + point.y) / 2;
          return `Q ${px} ${midY} ${cx} ${midY} T ${cx} ${point.y}`;
        })
        .join(" ")
    : "";
  const first = points[0];
  const last = points[points.length - 1];
  const firstX = clampX(first.x);
  const lastX = clampX(last.x);
  const firstGateX = Math.round((firstX / 100) * stageWidth);
  const lastGateX = Math.round((lastX / 100) * stageWidth);

  return (
    <div ref={stageRef} className="termap3d" role="list" aria-label={t("academic.mapLabel")} style={{ height }}>
      <svg className="termap3d__trail" aria-hidden="true">
        {stageWidth > 0 && (
          <>
            <path className="termap3d__trail-glow" d={trailPath} />
            <path className="termap3d__trail-line" d={trailPath} />
            <circle className="termap3d__gate termap3d__gate--start" cx={firstGateX} cy={first.y - 46} r="9" />
            <circle className="termap3d__gate termap3d__gate--end" cx={lastGateX} cy={last.y + 46} r="9" />
          </>
        )}
      </svg>
      <span className="termap3d__label termap3d__label--start" style={{ left: `${Math.min(Math.max(firstX, 18), 82)}%`, top: first.y - 74 }}>{t("academic.startFlag")}</span>
      <span className="termap3d__label termap3d__label--end" style={{ left: `${Math.min(Math.max(lastX, 18), 82)}%`, top: last.y + 60 }}>{t("academic.finishFlag")}</span>
      {nodes.map((node, index) => {
        const point = points[index];
        const isCurrent = node.status === "active" || activeId === node.id;
        return (
          <button
            key={node.id}
            role="listitem"
            className={`termap3d__node ${node.status === "completed" ? "is-done" : ""} ${isCurrent ? "is-current" : ""}`}
            style={{ left: `${clampX(point.x)}%`, top: point.y, "--i": index } as React.CSSProperties}
            onClick={() => onSelect(node.id)}
          >
            <span className="termap3d__island" aria-hidden="true" />
            <span className="termap3d__badge">{node.status === "completed" ? "✓" : formatNumber(index + 1)}</span>
            <span className="termap3d__name">{node.label}</span>
            <span className="termap3d__sub">{node.sub}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Term-end report popup: appears once per ended term; grades are judged by
 *  the university's country scale (Phase G). */
function TermEndReport({ termName, courses, onClose, onSaved }: {
  termName: string;
  courses: Course[];
  onClose: () => void;
  onSaved: (results: Array<{ course: string; grade: string; passed: boolean }>) => void;
}) {
  const { t } = useI18n();
  const settings = readSettings();
  const [scale, setScale] = useState<GradingScale>(scaleFor(settings.university));
  const [grades, setGrades] = useState<Record<string, string>>({});

  const results = courses
    .filter((course) => grades[course.id]?.trim())
    .map((course) => {
      const value = Number(String(grades[course.id]).replace(",", "."));
      const passed = Number.isFinite(value) ? scale.passes(value) : false;
      return { course: course.title, grade: Number.isFinite(value) ? scale.format(value) : grades[course.id], passed };
    });

  return createPortal(
    <div className="composer-backdrop" onClick={onClose} role="presentation">
      <div className="composer-modal rise termend" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="composer-modal__head">
          <h2>{t("academic.termEndTitle", { term: termName })}</h2>
          <button className="composer-modal__close" onClick={onClose} aria-label={t("common.cancel")}><Icon name="x" size={16} /></button>
        </div>
        <p className="tree-picker__hint">{t("academic.termEndLead")}</p>
        <SelectField label={t("academic.scaleLabel")} value={scale.id} onChange={(event) => setScale(GRADING_SCALES[event.target.value as GradingScale["id"]])}>
          {Object.values(GRADING_SCALES).map((entry) => <option key={entry.id} value={entry.id}>{entry.label}</option>)}
        </SelectField>
        <div className="termend__grades">
          {courses.length ? courses.map((course) => (
            <Field
              key={course.id}
              label={course.title}
              type="text"
              inputMode="decimal"
              value={grades[course.id] ?? ""}
              onChange={(event) => setGrades((current) => ({ ...current, [course.id]: event.target.value }))}
              placeholder={t("academic.gradePlaceholder")}
            />
          )) : <EmptyState icon="book" title={t("courses.emptyTitle")} />}
        </div>
        {results.length > 0 && (
          <div className="termend__results">
            {results.map((result) => (
              <div key={result.course} className={`termend__result ${result.passed ? "is-pass" : "is-fail"}`}>
                <span>{result.course}</span>
                <strong>{result.grade}</strong>
                <Badge tone={result.passed ? "green" : "amber"}>{result.passed ? t("academic.passed") : t("academic.failed")}</Badge>
              </div>
            ))}
          </div>
        )}
        <div className="composer-modal__actions">
          <Button type="button" variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => { onSaved(results); onClose(); }} disabled={!results.length}>{t("academic.saveGrades")}</Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function AcademicPathPage() {
  const { t, formatDate, formatNumber } = useI18n();
  const { courses } = useCourses();
  const [terms, setTerms] = useState<SemesterInfo[] | null>(null);
  const [termsFailed, setTermsFailed] = useState(false);
  const loadTerms = () => {
    setTermsFailed(false);
    setTerms(null);
    api<SemesterInfo[]>("/academic-path").then(setTerms).catch(() => setTermsFailed(true));
  };
  const [planned, setPlanned] = useState<PlannedSemester[]>(() => readPlanned());
  const [settings, setSettings] = useState<AcademicSettings>(() => readSettings());
  // Drafts: search as you type, persist only on Save (owner-requested flow).
  const [uniDraft, setUniDraft] = useState(settings.university);
  const [majorDraft, setMajorDraft] = useState(settings.major);
  const [countryDraft, setCountryDraft] = useState(settings.country || countryOf(settings.university));
  const [uniOpen, setUniOpen] = useState(false);
  const [savedTick, setSavedTick] = useState(false);
  const uniMatches = useMemo(
    () => (uniDraft.trim().length < 2 ? [] : UNIVERSITIES.filter((entry) =>
      entry.name.toLowerCase().includes(uniDraft.trim().toLowerCase())).slice(0, 6)),
    [uniDraft]
  );
  const foundUniversity: UniversityEntry | null = UNIVERSITIES.find((entry) => entry.name === uniDraft.trim()) ?? null;
  const uniDirty = uniDraft !== settings.university || majorDraft !== settings.major || countryDraft !== settings.country;
  // Smart system: the picked university's country (manual override wins).
  const activeCountry = countryDraft || countryOf(uniDraft);
  const system = systemFor(activeCountry);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState<PlannedSemester | null>(null);
  const [reportDismissed, setReportDismissed] = useState(false);
  const [manualReport, setManualReport] = useState<PlannedSemester | null>(null);

  useEffect(() => {
    if (isDemoMode()) { setTerms([]); return; }
    // A failed load used to collapse to [] — real terms rendered as "no terms"
    // (data looked deleted). Failed now surfaces as an error card with retry.
    loadTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSemester = (semester: PlannedSemester) => {
    const next = planned.some((item) => item.id === semester.id)
      ? planned.map((item) => (item.id === semester.id ? semester : item))
      : [...planned, semester];
    setPlanned(next);
    writePlanned(next);
  };

  const deleteSemester = (id: string) => {
    const next = planned.filter((item) => item.id !== id);
    setPlanned(next);
    writePlanned(next);
  };

  const persistSettings = (next: AcademicSettings) => {
    setSettings(next);
    try { window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const saveAcademic = () => {
    persistSettings({ ...settings, university: uniDraft.trim(), major: majorDraft.trim(), country: activeCountry });
    // Two-way sync: the server profile is the shared truth — settings/profile
    // shows the same university & major automatically.
    if (!isDemoMode()) {
      api("/auth/profile", { method: "PATCH", body: JSON.stringify({ university: uniDraft.trim(), program: majorDraft.trim() }) }).catch(() => undefined);
    }
    setSavedTick(true);
    window.setTimeout(() => setSavedTick(false), 2500);
  };
  // First load: the SERVER profile wins when it already knows university/major.
  useEffect(() => {
    if (isDemoMode()) return;
    api<{ university?: string; program?: string }>("/auth/profile")
      .then((profile) => {
        if (profile.university && !settings.university) setUniDraft(profile.university);
        if (profile.program && !settings.major) setMajorDraft(profile.program);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { setUniDraft(settings.university); setMajorDraft(settings.major); setCountryDraft(settings.country || countryOf(settings.university)); }, [settings.university, settings.major, settings.country]);

  const totalCourses = terms?.reduce((sum, term) => sum + term.courseCount, 0) ?? 0;
  const totalCredits = (terms?.reduce((sum, term) => sum + term.credits, 0) ?? 0)
    + planned.reduce((sum, term) => term.credits, 0);
  const activeIndex = terms?.findIndex((term) => term.status === "active") ?? -1;
  const plannedCredits = planned.reduce((sum, term) => term.credits, 0);

  // Map nodes: server terms first, then planned semesters — ordered like a game level path.
  const nodes = useMemo(() => {
    const merged = [
      ...(terms ?? []).map((term, index) => ({
        id: `srv-${term.id}`,
        order: index + 1,
        label: term.name,
        sub: `${formatNumber(term.credits)} ${t("academic.creditsShort")}`,
        status: term.status === "completed" ? "completed" : term.status === "active" || (activeIndex === -1 && index === 0) ? "active" : "planned"
      })),
      ...planned.map((semester) => ({
        id: semester.id,
        order: semester.termNumber || 99,
        label: semester.name,
        sub: `${formatNumber(semester.credits)} ${t("academic.creditsShort")}`,
        status: semester.status
      }))
    ];
    // The term NUMBER decides the position on the map — enter 3 and it lands
    // between 2 and 4 automatically.
    return merged.sort((a, b) => a.order - b.order);
  }, [terms, planned, activeIndex, t, formatNumber]);

  const selectedTerm = terms?.find((term) => `srv-${term.id}` === selectedNode);
  const selectedPlanned = planned.find((semester) => semester.id === selectedNode);

  // Term-end trigger — PERSISTENT policy (lib/termEnd): graded = never again,
  // dismissed = re-arms after the reprompt window or when the end date changes.
  // (The old sessionStorage marker wiped on browser close → the popup re-popped
  // on every visit — the owner-reported bug.)
  const termEnded = useMemo(() => {
    if (reportDismissed) return null;
    const candidates: TermEndCandidate[] = [
      ...(terms ?? []).map((term) => ({ id: `srv-${term.id}`, name: term.name, startsOn: term.startsOn ?? "", endsOn: (term as unknown as { endsOn?: string }).endsOn ?? "" })),
      ...planned.map((semester) => ({ id: semester.id, name: semester.name, startsOn: semester.startsOn, endsOn: semester.endsOn ?? "" }))
    ];
    return pickTermEnd(candidates, readTermEndState(), Date.now());
  }, [terms, planned, reportDismissed]);

  const openReport = Boolean(termEnded) && terms !== null;

  const statusTone = (status: string) => status === "completed" ? "green" : status === "active" ? "purple" : "neutral";

  return (
    <div className="page-stack rise">
      <PageHeader
        title={t("nav.academicPath")}
        subtitle={terms?.length
          ? t("academic.subtitle", { terms: formatNumber(terms.length), courses: formatNumber(totalCourses), credits: formatNumber(totalCredits) })
          : t("academic.subtitleEmpty")}
      />
      <SemesterComposer
        open={composerOpen}
        editing={editing}
        onClose={() => { setComposerOpen(false); setEditing(null); }}
        onSaved={saveSemester}
      />

      {/* University + major — live search over the world list, found-tick +
          Save button (the found entry decides the grading scale). */}
      <Card className="sem-planner">
        <div className="section-heading compact">
          <div><span className="section-kicker">{t("academic.universityKicker")}</span><h2>{t("academic.universityTitle")}</h2></div>
        </div>
        <Field
          label={t("academic.university")}
          value={uniDraft}
          onChange={(event) => { setUniDraft(event.target.value); setUniOpen(true); }}
          onFocus={() => setUniOpen(true)}
          placeholder={t("academic.universityPlaceholder")}
          autoComplete="off"
        />
        {uniOpen && uniMatches.length > 0 && (
          <div className="uni-suggest" role="listbox">
            {uniMatches.map((entry) => (
              <button
                key={entry.name}
                type="button"
                role="option"
                aria-selected={entry.name === uniDraft}
                className="uni-suggest__row"
                onClick={() => { setUniDraft(entry.name); setCountryDraft(entry.country); setUniOpen(false); }}
              >
                <strong>{entry.name}</strong>
                <small>{entry.country} · {GRADING_SCALES[entry.scale].label}</small>
              </button>
            ))}
          </div>
        )}
        {uniDraft.trim() !== "" && (
          foundUniversity
            ? (
              <div className="uni-found" role="status">
                <b>✓</b>
                <span>{t("academic.found", { name: foundUniversity.name, country: foundUniversity.country })}</span>
                <small>{GRADING_SCALES[foundUniversity.scale].label}</small>
              </div>
            )
            : <div className="uni-found uni-found--miss" role="status"><span>{t("academic.notFound")}</span></div>
        )}
        <SelectField
          label={t("academic.country")}
          value={activeCountry}
          onChange={(event) => setCountryDraft(event.target.value)}
        >
          <option value="">{t("academic.countryAuto")}</option>
          {KNOWN_COUNTRIES.map((code) => <option key={code} value={code}>{code}</option>)}
        </SelectField>
        {system && (
          <div className="uni-system" role="status">
            <strong>{t("academic.systemTitle")}</strong>
            <small>
              {t("academic.systemTerms", { count: formatNumber(system.termsPerYear), label: system.termsLabel })} · {t("academic.systemCredits", { credits: formatNumber(system.creditsPerTerm) })} · {t("academic.systemTotal", { total: formatNumber(system.totalBachelor) })}
            </small>
          </div>
        )}
        <Field label={t("academic.major")} value={majorDraft} onChange={(event) => setMajorDraft(event.target.value)} placeholder={t("academic.majorPlaceholder")} />
        <div className="uni-save">
          <Button icon="check" disabled={!uniDirty} onClick={saveAcademic}>{uniDirty ? t("academic.saveSettings") : t("academic.savedSettings")}</Button>
          {savedTick && <span className="uni-save__tick" role="status">✓ {t("academic.savedSettings")}</span>}
        </div>
      </Card>

      {terms === null ? <Card><EmptyState icon="route" title={termsFailed ? t("common.error") : t("common.loading")} description={termsFailed ? t("common.offline") : undefined} action={termsFailed ? <Button icon="check" onClick={loadTerms}>{t("common.retry")}</Button> : undefined} /></Card>
        : nodes.length ? (
          <Card className="academic-path tilt-card">
            <div className="section-heading compact">
              <div><span className="section-kicker">{t("academic.mapKicker")}</span><h2>{t("academic.mapTitle")}</h2></div>
              <Button icon="plus" onClick={() => { setEditing(null); setComposerOpen(true); }}>{t("sem.add")}</Button>
            </div>
            <TermMap nodes={nodes} activeId={selectedNode} onSelect={(id) => setSelectedNode((current) => (current === id ? null : id))} />
            {(selectedTerm || selectedPlanned) && (
              <div className="academic-path__detail">
                <strong>{selectedTerm?.name ?? selectedPlanned?.name}</strong>
                <small>
                  {(selectedTerm?.courseCount ?? 0) > 0 && t("academic.courseCount", { count: formatNumber(selectedTerm!.courseCount) })}
                  {(selectedTerm?.credits ?? selectedPlanned?.credits ?? 0) > 0 && ` · ${t("academic.credits", { count: formatNumber(selectedTerm?.credits ?? selectedPlanned?.credits ?? 0) })}`}
                  {(selectedTerm?.startsOn ?? selectedPlanned?.startsOn) && ` · ${formatDate(selectedTerm?.startsOn ?? selectedPlanned?.startsOn ?? "", { day: "numeric", month: "short", year: "numeric" })}`}
                </small>
                {selectedPlanned && (
                  <div className="simple-task__actions">
                    <button onClick={() => { setEditing(selectedPlanned); setComposerOpen(true); }} aria-label={t("common.edit")}><Icon name="edit" size={14} /></button>
                    <button onClick={() => { deleteSemester(selectedPlanned.id); setSelectedNode(null); }} aria-label={t("common.delete")}><Icon name="trash" size={14} /></button>
                  </div>
                )}
              </div>
            )}
            <Badge tone={activeIndex >= 0 ? "purple" : "neutral"}>
              {activeIndex >= 0 ? t("academic.currentIs", { name: terms![activeIndex].name }) : t("academic.noActive")}
            </Badge>
          </Card>
        ) : (
          <Card><EmptyState icon="route" title={t("academic.emptyTitle")} description={t("academic.emptyBody")} /></Card>
        )}

      <Card className="sem-planner">
        <div className="section-heading compact">
          <div><span className="section-kicker">{t("nav.academicPath")}</span><h2>{t("sem.plannerTitle")}</h2></div>
          <Button icon="plus" onClick={() => { setEditing(null); setComposerOpen(true); }}>{t("sem.add")}</Button>
        </div>
        <p className="tree-picker__hint">{t("sem.plannerLead", { count: formatNumber(planned.length), credits: formatNumber(plannedCredits) })}</p>
        {planned.length > 0 && (
          <div className="sem-cards">
            {planned.map((semester) => (
              <div key={semester.id} className="sem-card-wrap">
                <button className="sem-card" onClick={() => { setEditing(semester); setComposerOpen(true); }}>
                  <strong>{semester.name}</strong>
                  <small>{t("academic.credits", { count: formatNumber(semester.credits) })}</small>
                  <Badge tone={statusTone(semester.status)}>{t(`academic.status.${semester.status}`)}</Badge>
                  {semester.status !== "completed" && (
                    <span
                      className="sem-card__close"
                      role="button"
                      tabIndex={0}
                      aria-label={t("sem.closeTerm")}
                      onClick={(event) => { event.stopPropagation(); setManualReport(semester); }}
                      onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.stopPropagation(); setManualReport(semester); } }}
                    >
                      {t("sem.closeTerm")}
                    </span>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {manualReport && (
        <TermEndReport
          termName={manualReport.name}
          courses={courses}
          onClose={() => setManualReport(null)}
          onSaved={(results) => {
            try {
              const all = JSON.parse(window.localStorage.getItem(GRADES_KEY) ?? "{}");
              all[manualReport.id] = results;
              window.localStorage.setItem(GRADES_KEY, JSON.stringify(all));
            } catch { /* ignore */ }
            const next = planned.map((semester) => (semester.id === manualReport.id ? { ...semester, status: "completed" as const } : semester));
            setPlanned(next);
            writePlanned(next);
            setManualReport(null);
          }}
        />
      )}
      {openReport && termEnded && (
        <TermEndReport
          termName={termEnded.name}
          courses={courses}
          onClose={() => {
            markTermEnd(window.localStorage, termEnded.id, termEnded.endsOn, "dismissed", Date.now());
            setReportDismissed(true);
          }}
          onSaved={(results) => {
            try {
              const all = JSON.parse(window.localStorage.getItem(GRADES_KEY) ?? "{}");
              all[termEnded.id] = results;
              window.localStorage.setItem(GRADES_KEY, JSON.stringify(all));
            } catch { /* ignore */ }
            markTermEnd(window.localStorage, termEnded.id, termEnded.endsOn, "graded", Date.now());
            setReportDismissed(true);
          }}
        />
      )}
    </div>
  );
}
